import React from "react"
import { Grid, Spacer, ToolTip } from "@cd2/components"
import { MultiButton } from "./MultiButton"
import { TextBlock, P, H2 } from "@cd2/text"
import { Card } from "./Card"

interface IProps<T> {
  title?: React.ReactNode
  subtitle?: string
  tabs: {
    key: T
    title: string
    content: React.ReactNode
    disabled?: boolean
    disabledReason?: string
  }[]
  onTabChange(key: T): void
  currentTab: T
}
export function TabbedSection<T>({ title, subtitle, tabs, currentTab, onTabChange }: IProps<T>) {
  function handleChange(key: T) {
    if (key !== currentTab) {
      onTabChange(key)
    }
  }

  const activeTab = tabs.find(tab => tab.key === currentTab)
  const showTabButtons = tabs.length > 1

  return (
    <FormSection title={title} subtitle={subtitle}>
      {showTabButtons && (
        <React.Fragment>
          <MultiButton
            buttons={tabs.map(tab => ({
              buttonStyle: currentTab === tab.key ? "solid" : "outline",
              onClick: () => handleChange(tab.key),
              extendTheme: {
                padding: 7,
                fontSize: 12,
              },
              children: tab.title,
            }))}
            asGroup
            allowWrap
          />
          <Spacer size={24} />
        </React.Fragment>
      )}
      {activeTab && activeTab.content}
    </FormSection>
  )
}

export function TabsList<T extends string>({
  tabs,
  currentTab,
  onTabChange,
}: {
  tabs: { key: T; title: string; disabled?: boolean; disabledReason?: string }[]
  currentTab: string
  onTabChange(key: string): void
}) {
  function handleChange(key: T) {
    if (key !== currentTab) {
      onTabChange(key)
    }
  }

  return (
    <div>
      <MultiButton
        buttons={tabs.map(tab => ({
          buttonStyle: currentTab === tab.key ? "solid" : "outline",
          onClick: () => handleChange(tab.key),
          disabled: tab.disabled,
          Wrapper: tab.disabledReason
            ? ({ children }) => (
                <ToolTip hoverContent={() => <P size="small">{tab.disabledReason}</P>}>
                  {children}
                </ToolTip>
              )
            : undefined,
          extendTheme: {
            padding: 7,
            fontSize: 12,
          },
          children: tab.title,
        }))}
        asGroup
        allowWrap
      />
    </div>
  )
}

interface IFormSectionProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  children: React.ReactNode
  gap?: boolean
}
export function FormSection({ title, subtitle, children, gap }: IFormSectionProps) {
  return (
    <Card secondary>
      <TextBlock blockSpacing={0}>
        <H2>{title}</H2>
        <P color="primary">{subtitle}</P>
      </TextBlock>
      <Spacer size={16} />
      {gap && <Grid gap={8}>{children}</Grid>}
      {!gap && children}
    </Card>
  )
}
